import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { PiMoneyWavyLight, PiNoteBlankLight } from "react-icons/pi";
import { FiInfo } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { fetchMyPermissions, updateUser } from "../../../redux/slices/userSlice";

const CONTRACT_END_REQUIRED_TYPES = new Set(["Fixed Term", "Internship"]);
const CONTRACT_FIELDS = [
  "salary",
  "employment_type",
  "hiring_date",
  "contract_type",
  "contract_end_date",
];

const toDateInputValue = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const toSalaryInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

const buildFormData = (user) => ({
  salary: toSalaryInputValue(user.salary),
  employment_type: user.employment_type || "",
  hiring_date: toDateInputValue(user.hiring_date),
  contract_type: user.contract_type || "",
  contract_end_date: toDateInputValue(user.contract_end_date),
});

const EmployeeContractsTab = ({ currentUser, userId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loggedInUser = useSelector((state) => state.auth?.user);
  const permissions = useSelector((state) => state.users?.permissions || {});
  const isOwner = String(loggedInUser?.id) === String(userId);
  const canEdit =
    permissions.can_update_users === true &&
    (!isOwner || permissions.can_delete_users === true);

  const [formData, setFormData] = useState({});
  const [initialFormData, setInitialFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showCompensationInfo, setShowCompensationInfo] = useState(false);

  useEffect(() => {
    dispatch(fetchMyPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (!currentUser) return;

    const nextFormData = buildFormData(currentUser);
    setFormData(nextFormData);
    setInitialFormData(nextFormData);
    setValidationAttempted(false);
    setSaveError("");
  }, [currentUser]);

  const contractTypeOptions = useMemo(() => {
    const options = [
      {
        value: "Open Ended",
        label: t("enums.contractType.openEnded", { defaultValue: "Sem Termo" }),
      },
      {
        value: "Fixed Term",
        label: t("enums.contractType.fixedTerm", { defaultValue: "Termo Certo" }),
      },
      {
        value: "Internship",
        label: t("enums.contractType.internship", { defaultValue: "Estágio" }),
      },
    ];

    if (
      formData.contract_type &&
      !options.some((option) => option.value === formData.contract_type)
    ) {
      options.push({ value: formData.contract_type, label: formData.contract_type });
    }

    return options;
  }, [formData.contract_type, t]);

  const employmentTypeOptions = useMemo(() => {
    const options = [
      {
        value: "Full Time",
        label: t("enums.employmentType.fullTime", { defaultValue: "Full Time" }),
      },
      {
        value: "Part Time",
        label: t("enums.employmentType.partTime", { defaultValue: "Part Time" }),
      },
      {
        value: "Contract",
        label: t("enums.employmentType.freelancer", { defaultValue: "Freelancer" }),
      },
      {
        value: "Intern",
        label: t("enums.employmentType.internship", { defaultValue: "Estágio" }),
      },
    ];

    if (
      formData.employment_type &&
      !options.some((option) => option.value === formData.employment_type)
    ) {
      options.push({ value: formData.employment_type, label: formData.employment_type });
    }

    return options;
  }, [formData.employment_type, t]);

  const salaryText = String(formData.salary ?? "").trim();
  const salaryNumber = salaryText === "" ? null : Number(salaryText);
  const salaryInvalid =
    salaryText !== "" && (!Number.isFinite(salaryNumber) || salaryNumber < 0);
  const employmentTypeMissing = !formData.employment_type;
  const endDateMissing =
    CONTRACT_END_REQUIRED_TYPES.has(formData.contract_type) && !formData.contract_end_date;
  const showSalaryError = salaryInvalid && (validationAttempted || salaryText !== "");
  const showEmploymentTypeError = validationAttempted && employmentTypeMissing;
  const showEndDateError =
    endDateMissing && (validationAttempted || CONTRACT_END_REQUIRED_TYPES.has(formData.contract_type));

  const hasChanges = useMemo(
    () =>
      CONTRACT_FIELDS.some(
        (field) => (formData[field] ?? "") !== (initialFormData[field] ?? "")
      ),
    [formData, initialFormData]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;

    setSaveError("");
    setFormData((previous) => {
      const next = { ...previous, [name]: value };

      // An open-ended contract must never keep a stale end date.
      if (name === "contract_type" && value === "Open Ended") {
        next.contract_end_date = "";
      }

      return next;
    });
  };

  const handleSave = async (event) => {
    event?.preventDefault();
    if (!userId || !canEdit) return;

    setValidationAttempted(true);
    setSaveError("");

    if (salaryInvalid) {
      toast.error(
        t("components.contracts.errors.invalidSalary", {
          defaultValue: "O salário deve ser um número igual ou superior a zero.",
        })
      );
      return;
    }

    if (employmentTypeMissing) {
      toast.error(
        t("components.contracts.errors.missingEmploymentType", {
          defaultValue: "Selecione o tipo de emprego.",
        })
      );
      return;
    }

    if (endDateMissing) {
      toast.error(
        t("components.contracts.errors.missingEndDate", {
          defaultValue: "Selecione a data de término para este tipo de contrato.",
        })
      );
      return;
    }

    if (!hasChanges) return;

    const updates = {};
    if (salaryText !== (initialFormData.salary ?? "")) {
      updates.salary = salaryNumber;
    }
    if (formData.employment_type !== (initialFormData.employment_type ?? "")) {
      updates.employment_type = formData.employment_type;
    }
    if (formData.hiring_date !== (initialFormData.hiring_date ?? "")) {
      updates.hiring_date = formData.hiring_date || null;
    }
    if (formData.contract_type !== (initialFormData.contract_type ?? "")) {
      updates.contract_type = formData.contract_type || null;
    }
    if (formData.contract_end_date !== (initialFormData.contract_end_date ?? "")) {
      updates.contract_end_date = formData.contract_end_date || null;
    }
    setIsSaving(true);
    try {
      // updateUser already reports success/failure through the global toast slice.
      await dispatch(updateUser({ userId, updates })).unwrap();
      setInitialFormData({ ...formData });
    } catch (error) {
      const message =
        (typeof error === "string" && error) ||
        error?.detail ||
        t("components.contracts.errors.save", {
          defaultValue: "Não foi possível guardar o contrato.",
        });
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentUser) return null;

  return (
    <div className="d-flex flex-column gap-4 w-100 pt-3">
      {saveError && (
        <Alert variant="danger" className="mb-0">
          {saveError}
        </Alert>
      )}

      <Card className="atlar-card p-4">
        <Card.Body className="p-0">
          <div className="d-flex align-items-center gap-2 mb-4">
            <PiMoneyWavyLight className="fs-4 text-danger" />
            <h5 className="mb-0 fw-bold txt-main">
              {t("components.contracts.compensation", { defaultValue: "Compensação" })}
            </h5>
          </div>

          <Form onSubmit={handleSave} noValidate>
            <Row className="mb-4">
              <Col md={4}>
                <Form.Group>
                  <Form.Label
                    htmlFor="employee-contract-salary"
                    className="txt-subtle fw-semibold"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {t("components.contracts.baseSalary", {
                      defaultValue: "Salário base Anual (€)",
                    })}
                  </Form.Label>
                  <Form.Control
                    id="employee-contract-salary"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    name="salary"
                    value={formData.salary ?? ""}
                    onChange={handleChange}
                    className={`bg-surface txt-main fs-5 ${showSalaryError ? "is-invalid" : ""}`}
                    disabled={!canEdit}
                    placeholder="Ex: 25000.00"
                    aria-describedby={
                      showSalaryError ? "employee-contract-salary-error" : undefined
                    }
                  />
                  {showSalaryError && (
                    <div id="employee-contract-salary-error" className="invalid-feedback d-block">
                      {t("components.contracts.errors.invalidSalary", {
                        defaultValue: "O salário deve ser um número igual ou superior a zero.",
                      })}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>

          <div
            className="d-flex align-items-center justify-content-between p-3 rounded mt-3"
            style={{ backgroundColor: "#fdf8ec", border: "1px solid #f6e5c0" }}
          >
            <div>
              <div className="fw-bold text-dark mb-1" style={{ fontSize: "0.9rem" }}>
                {t("components.contracts.compensationBanner.title", {
                  defaultValue: "Processe mais do que o salário base",
                })}
              </div>
              <div className="text-dark" style={{ fontSize: "0.85rem" }}>
                {t("components.contracts.compensationBanner.description", {
                  defaultValue:
                    "A compensação sincroniza automaticamente os pagamentos variáveis com o seu software de recibos ou contabilista.",
                })}
              </div>
            </div>
            <Button
              type="button"
              variant="light"
              size="sm"
              className="d-flex align-items-center gap-1 bg-white border shadow-sm"
              onClick={() => setShowCompensationInfo((visible) => !visible)}
              aria-expanded={showCompensationInfo}
              aria-controls="employee-compensation-info"
            >
              <FiInfo className="text-warning" />
              {t("components.contracts.moreInfo", { defaultValue: "Mais informações" })}
            </Button>
          </div>

          {showCompensationInfo && (
            <div
              id="employee-compensation-info"
              className="mt-3 p-3 rounded bg-surface border text-muted"
              style={{ fontSize: "0.85rem" }}
            >
              {t("components.contracts.compensationBanner.details", {
                defaultValue:
                  "Os pagamentos variáveis são sincronizados automaticamente após a atualização dos dados do contrato.",
              })}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="atlar-card p-4">
        <Card.Body className="p-0">
          <div className="d-flex align-items-center gap-2 mb-4">
            <PiNoteBlankLight className="fs-4 text-danger" />
            <h5 className="mb-0 fw-bold txt-main">
              {t("components.contracts.details", { defaultValue: "Detalhes do Contrato" })}
            </h5>
          </div>

          <Form onSubmit={handleSave} noValidate>
            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label
                    htmlFor="employee-employment-type"
                    className="txt-subtle fw-semibold"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {t("components.contracts.employmentType", {
                      defaultValue: "Tipo de emprego",
                    })}
                  </Form.Label>
                  <Form.Select
                    id="employee-employment-type"
                    name="employment_type"
                    value={formData.employment_type || ""}
                    onChange={handleChange}
                    className={`bg-surface txt-main ${showEmploymentTypeError ? "is-invalid" : ""}`}
                    disabled={!canEdit}
                    aria-describedby={
                      showEmploymentTypeError ? "employee-employment-type-error" : undefined
                    }
                  >
                    <option value="" disabled>
                      {t("common.select", "Selecione...")}
                    </option>
                    {employmentTypeOptions.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                  {showEmploymentTypeError && (
                    <div id="employee-employment-type-error" className="invalid-feedback d-block">
                      {t("components.contracts.errors.missingEmploymentType", {
                        defaultValue: "Selecione o tipo de emprego.",
                      })}
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label
                    htmlFor="employee-contract-type"
                    className="txt-subtle fw-semibold"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {t("components.contracts.contractType", {
                      defaultValue: "Tipo de Contrato",
                    })}
                  </Form.Label>
                  <Form.Select
                    id="employee-contract-type"
                    name="contract_type"
                    value={formData.contract_type || ""}
                    onChange={handleChange}
                    className="bg-surface txt-main"
                    disabled={!canEdit}
                  >
                    <option value="" disabled>
                      {t("common.select", "Selecione...")}
                    </option>
                    {contractTypeOptions.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label
                    htmlFor="employee-hiring-date"
                    className="txt-subtle fw-semibold"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {t("components.contracts.hiringDate", {
                      defaultValue: "Data de contratação",
                    })}
                  </Form.Label>
                  <Form.Control
                    id="employee-hiring-date"
                    type="date"
                    name="hiring_date"
                    value={formData.hiring_date || ""}
                    onChange={handleChange}
                    className="bg-surface txt-main"
                    disabled={!canEdit}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label
                    htmlFor="employee-contract-end-date"
                    className="txt-subtle fw-semibold"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {t("components.contracts.endDate", { defaultValue: "Data de Término" })}
                  </Form.Label>
                  <Form.Control
                    id="employee-contract-end-date"
                    type="date"
                    name="contract_end_date"
                    value={formData.contract_end_date || ""}
                    onChange={handleChange}
                    className={`bg-surface txt-main ${showEndDateError ? "is-invalid" : ""}`}
                    disabled={!canEdit || formData.contract_type === "Open Ended"}
                    aria-describedby={
                      showEndDateError ? "employee-contract-end-date-error" : undefined
                    }
                  />
                  {showEndDateError && (
                    <div id="employee-contract-end-date-error" className="invalid-feedback d-block">
                      {t("components.contracts.errors.missingEndDate", {
                        defaultValue: "Selecione a data de término para este tipo de contrato.",
                      })}
                    </div>
                  )}
                </Form.Group>
              </Col>

            </Row>
          </Form>

          <div className="mt-4">
            <DetailRow
              label={t("components.contracts.seniorityCalculated", { defaultValue: "Antiguidade calculada" })}
              value={currentUser.seniority_level || t("components.contracts.notDefined", "Não definido")}
            />
          </div>
        </Card.Body>
      </Card>

      <Card className="atlar-card p-4">
        <Card.Body className="p-0">
          <h5 className="mb-4 fw-bold txt-main">
            {t("components.contracts.policy.title", { defaultValue: "Política de remuneração" })}
          </h5>
          <DetailRow
            label={t("components.contracts.policy.overtimeRule", { defaultValue: "Regra de horas extra" })}
            value={t("components.contracts.policy.timeBank", { defaultValue: "Banco de horas" })}
          />
          <DetailRow
            label={t("components.contracts.policy.absenceApproval", { defaultValue: "Aprovação de ausências" })}
            value={t("components.contracts.policy.yes", { defaultValue: "Sim" })}
          />
          <DetailRow
            label={t("components.contracts.policy.shiftChangeApproval", { defaultValue: "Aprovação de alterações de turno" })}
            value={t("components.contracts.policy.yes", { defaultValue: "Sim" })}
          />
          <small className="txt-subtle d-block mt-2" style={{ fontSize: "0.8rem" }}>
            {t(
              "components.contracts.policy.locked",
              "As políticas de remuneração são definidas ao nível da organização."
            )}
          </small>
        </Card.Body>
      </Card>

      {canEdit && (
        <div className="d-flex justify-content-end mt-2">
          <Button
            type="button"
            variant="primary"
            className="rounded-pill px-4 py-2"
            onClick={handleSave}
            disabled={
              isSaving ||
              !hasChanges ||
              endDateMissing ||
              salaryInvalid ||
              employmentTypeMissing
            }
            style={{ backgroundColor: "var(--brand-primary)", border: "none" }}
          >
            {isSaving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t("common.saving", "A guardar...")}
              </>
            ) : (
              t("common.save_changes", "Guardar Alterações")
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="mb-3 d-flex justify-content-between align-items-center gap-3 border-bottom pb-2">
    <span className="txt-subtle">{label}</span>
    <span className="fw-semibold txt-main text-end">{value}</span>
  </div>
);

export default EmployeeContractsTab;
